// apm-onboarding.js
import isEqual from "lodash/isEqual";
import { LANGUAGES, OPERATING_SYSTEMS } from "onboarding/apm/installTypes";

const PERSIST_TO_COLUMN = "technology";

export const INITIAL_STATE = {
  serviceName: "",
  selectedLanguage: null,
  selectedOperatingSystem: null,
  connected: false,
  countdown: 20,
  timerStarted: false,
  initialized: false,
  supportedLanguages: [],
  supportedOperatingSystems: [],
  sample: false,
};

// helpers
const osSupportByLanguage = {};
const supportedLanguages = Object.keys(LANGUAGES)
  .map(languageId => {
    const language = LANGUAGES[languageId];
    if (!language) return {};
    const { supportedOperatingSystems = [] } = language;
    const validOperatingSystems = supportedOperatingSystems.filter(
      osId => OPERATING_SYSTEMS[osId]
    );
    osSupportByLanguage[languageId] = validOperatingSystems;
    return {
      languageId,
      ...language,
      supportedOperatingSystems: validOperatingSystems,
    };
  })
  .filter(language => language);

export const validApmSelections = apmState => {
  const { selectedLanguage, selectedOperatingSystem } = apmState;
  const supportedOperatingSystems = osSupportByLanguage[selectedLanguage] || [];
  const validSelections = { supportedLanguages, supportedOperatingSystems };
  if (supportedOperatingSystems.length) {
    validSelections.selectedLanguage = selectedLanguage;
    if (supportedOperatingSystems.indexOf(selectedOperatingSystem) >= 0) {
      validSelections.selectedOperatingSystem = selectedOperatingSystem;
    } else if (supportedOperatingSystems.length === 1) {
      // if only 1 operating system is supported, automatically select it
      validSelections.selectedOperatingSystem = supportedOperatingSystems[0];
    }
  }
  return validSelections;
};

export const apmStateEmpty = onboardingState =>
  !(onboardingState || {})[PERSIST_TO_COLUMN];

export const serializeApmState = (state = {}, compareTo = {}) => {
  // serialize to array for saving to onboarding state
  const {
    connected,
    countdown,
    initialized,
    reset,
    serviceName: name,
    selectedLanguage: language,
    selectedOperatingSystem: os,
    timerStarted,
  } = state;
  if (reset) return { [PERSIST_TO_COLUMN]: null };
  if (!initialized) return {};
  const status = connected
    ? `${timerStarted ? countdown : "connected"}`
    : "waiting";
  const stateArray = [name, language, os, status];
  if (isEqual(stateArray, compareTo[PERSIST_TO_COLUMN])) {
    return {};
  }
  return { [PERSIST_TO_COLUMN]: stateArray };
};

export const deserializeApmState = onboardingState => {
  // deserialize from array read from onboarding state
  const serializedState = (onboardingState || {})[PERSIST_TO_COLUMN] || [];
  const [name, language, os, status] = serializedState;
  const timerStarted = /^\d+$/.test(status);
  const connected = timerStarted || status === "connected";
  const countdown = timerStarted ? Number(status) : INITIAL_STATE.countdown;
  return {
    ...INITIAL_STATE,
    serviceName: name,
    ...validApmSelections({
      selectedLanguage: language,
      selectedOperatingSystem: os,
    }),
    connected,
    countdown,
    timerStarted: countdown === 0,
  };
};

// action types
const SET_APM_ONBOARDING_STATE =
  "appoptics/apm-onboarding/SET_APM_ONBOARDING_STATE";
const SET_SERVICE_NAME = "appoptics/apm-onboarding/SET_SERVICE_NAME";
const SET_LANGUAGE = "appoptics/apm-onboarding/SET_LANGUAGE";
const SET_OPERATING_SYSTEM = "appoptics/apm-onboarding/SET_OPERATING_SYSTEM";
const SET_CONNECTED = "appoptics/apm-onboarding/SET_CONNECTED";
const SET_SAMPLE = "appoptics/apm-onboarding/SET_SAMPLE";
const TICK = "appoptics/apm-onboarding/TICK";
const INVALIDATE = "appoptics/apm-onboarding/INVALIDATE";

// action creators
export const setApmOnboardingState = onboardingState => {
  return {
    type: SET_APM_ONBOARDING_STATE,
    onboardingState,
  };
};

export const setServiceName = serviceName => {
  return {
    type: SET_SERVICE_NAME,
    serviceName,
  };
};

export const setLanguage = selectedLanguage => {
  return {
    type: SET_LANGUAGE,
    selectedLanguage,
  };
};

export const setSample = (sample = true) => {
  return {
    type: SET_SAMPLE,
    sample,
  };
};

export const setOperatingSystem = selectedOperatingSystem => {
  return {
    type: SET_OPERATING_SYSTEM,
    selectedOperatingSystem,
  };
};

export const setConnected = (connected = true) => {
  return {
    type: SET_CONNECTED,
    connected,
  };
};

export const tick = () => {
  return {
    type: TICK,
  };
};

export const invalidateApmState = () => ({
  type: INVALIDATE,
});

// reducer
const apmOnboardingReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SET_APM_ONBOARDING_STATE:
      return {
        ...state,
        ...deserializeApmState(action.onboardingState),
        initialized: true,
      };
    case SET_SERVICE_NAME:
      return {
        ...state,
        serviceName: action.serviceName,
        initialized: true,
      };
    case SET_LANGUAGE:
      return {
        ...state,
        ...validApmSelections(action),
        initialized: true,
      };
    case SET_OPERATING_SYSTEM:
      return {
        ...state,
        selectedOperatingSystem: action.selectedOperatingSystem,
        initialized: true,
      };
    case SET_CONNECTED:
      return {
        ...state,
        connected: action.connected,
      };
    case SET_SAMPLE:
      return {
        ...state,
        sample: action.sample,
      };
    case TICK:
      return {
        ...state,
        countdown: state.countdown < 1 ? 0 : state.countdown - 1,
        timerStarted: true,
        initialized: true,
      };
    case INVALIDATE:
      return {
        ...state,
        initialized: false,
      };
    default:
      return state;
  }
};
export default apmOnboardingReducer;
